import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material'
import {
  Science as ScienceIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material'
import { formatAmount, calculateChemicalImpact } from '../../utils/chemicalCalculations'
import { getAvailableUnits, convertUnit } from '../../utils/unitConversions'
import { CHEMICAL_DATA } from '../../utils/constants'
import { toLocalDateTimeString, fromLocalDateTimeString } from '../../utils/dateHelpers'

const ChemicalAdditionForm = ({ 
  open, 
  onClose, 
  onSave, 
  recommendation,
  loading = false,
  poolConfig = null,
  currentMeasurement = null
}) => {
  const [formData, setFormData] = useState({
    amount: '',
    unit: '',
    notes: '',
    added_at: toLocalDateTimeString() // YYYY-MM-DDTHH:MM format in local timezone
  })
  const [availableUnits, setAvailableUnits] = useState([])
  const [error, setError] = useState('')
  const [calculatedImpact, setCalculatedImpact] = useState(null)

  useEffect(() => {
    if (open && recommendation) {
      const chemicalData = CHEMICAL_DATA[recommendation.chemical]
      const units = chemicalData ? getAvailableUnits(chemicalData.unit) : []
      
      setFormData({
        amount: recommendation.amount.toFixed(2),
        unit: recommendation.unit,
        notes: '',
        added_at: toLocalDateTimeString()
      })
      setAvailableUnits(units)
      setError('')
      
      // Calculate initial impact
      if (poolConfig && currentMeasurement) {
        const impact = calculateChemicalImpact(
          recommendation.chemical,
          recommendation.amount,
          poolConfig,
          currentMeasurement
        )
        setCalculatedImpact(impact)
      }
    }
  }, [open, recommendation, poolConfig, currentMeasurement])

  const handleChange = (e) => {
    const { name, value } = e.target
    
    if (name === 'unit') {
      // Convert the current amount to the new unit
      const currentAmount = parseFloat(formData.amount)
      if (!isNaN(currentAmount) && currentAmount > 0 && formData.unit) {
        const convertedAmount = convertUnit(currentAmount, formData.unit, value)
        if (convertedAmount !== null) {
          setFormData(prev => ({
            ...prev,
            amount: convertedAmount.toFixed(2),
            unit: value
          }))
        } else {
          setFormData(prev => ({ ...prev, unit: value }))
        }
      } else {
        setFormData(prev => ({ ...prev, unit: value }))
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
    
    // Recalculate impact when amount changes
    if (name === 'amount' && recommendation && poolConfig && currentMeasurement) {
      const amount = parseFloat(value)
      if (!isNaN(amount) && amount > 0) {
        // Convert to original unit for calculation if needed
        let calculationAmount = amount
        if (formData.unit !== recommendation.unit) {
          const converted = convertUnit(amount, formData.unit, recommendation.unit)
          if (converted !== null) {
            calculationAmount = converted
          }
        }
        
        const impact = calculateChemicalImpact(
          recommendation.chemical,
          calculationAmount,
          poolConfig,
          currentMeasurement
        )
        setCalculatedImpact(impact)
      } else {
        setCalculatedImpact(null)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    const amount = parseFloat(formData.amount)
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0')
      return
    }

    try {
      // Convert amount back to original unit for storage if needed
      let storageAmount = amount
      if (formData.unit !== recommendation.unit) {
        const converted = convertUnit(amount, formData.unit, recommendation.unit)
        if (converted !== null) {
          storageAmount = converted
        }
      }
      
      const additionData = {
        chemical: recommendation.chemical,
        chemicalName: recommendation.chemicalName,
        amount: storageAmount,
        unit: recommendation.unit, // Store in original unit
        added_at: fromLocalDateTimeString(formData.added_at),
        notes: formData.notes.trim(),
        recommendation: recommendation // Include original recommendation for tracking
      }

      await onSave(additionData)
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to save chemical addition')
    }
  }

  if (!recommendation) return null

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ScienceIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" component="div">
              Add Chemical
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {recommendation.chemicalName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Recommendation Summary */}
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: 'primary.light', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.main'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {recommendation.isRecommended ? 'Recommendation Summary' : 'Chemical Addition'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {recommendation.issue}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Expected Effect: {calculatedImpact ? calculatedImpact.description : recommendation.expectedEffect}
              </Typography>
            </Box>

            {/* Amount and Unit Selection */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="amount"
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                required
                variant="outlined"
                inputProps={{
                  step: '0.001',
                  min: '0',
                  max: '10000'
                }}
                helperText={recommendation.isRecommended ? `Recommended: ${formatAmount(recommendation.amount, recommendation.unit)}` : 'Enter the amount you want to add'}
                sx={{
                  flex: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <FormControl 
                variant="outlined" 
                sx={{ 
                  flex: 1,
                  minWidth: 120,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <InputLabel>Unit</InputLabel>
                <Select
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  label="Unit"
                  required
                >
                  {availableUnits.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Date/Time Input */}
            <TextField
              name="added_at"
              label="Date & Time Added"
              type="datetime-local"
              value={formData.added_at}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            {/* Notes Input */}
            <TextField
              name="notes"
              label="Notes (Optional)"
              placeholder="Any additional notes about this addition..."
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            {/* Dynamic impact display */}
            {calculatedImpact && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Calculated Impact:</strong> {calculatedImpact.description}
                </Typography>
              </Alert>
            )}
            
            {/* Warning if applicable */}
            {recommendation.warning && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Safety Note:</strong> {recommendation.warning}
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? null : <SaveIcon />}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 120,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            {loading ? 'Updating...' : 'Update Chemical'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default ChemicalAdditionForm